import React, { useState } from 'react';
import {
  useParams,
  Navigate,
  useNavigate,
  useRouter,
  useCanGoBack,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import {
  PageHeader,
  SharedAlertDialog,
  useAlertDialog,
} from '@/shared/components';
import { PageSkeleton } from '@/shared/components/PageSkeleton/PageSkeleton';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/lib/shadcn/components/ui/card';
import { CustomerForm, CustomerFormData } from '../components/CustomerForm';
import { useCustomerManager } from '../managers';
import type { CustomerDetail } from '../models/customer.model';
import { useUnsavedChangesBlocker } from '@/shared';

export const CustomerEditPage: React.FC = () => {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const id = params.id as string;
  const { customer, loading: isFetching, update, updateMutation } = useCustomerManager(id);
  const { t } = useTranslation('customers');
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const confirmDialog = useAlertDialog();
  const { handleConfirmLeave, handleCancelLeave } = useUnsavedChangesBlocker({
    isDirty,
    confirmDialog,
  });
  const { isOpen: isAlertOpen, hideAlert } = confirmDialog;

  const handleGoBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      router.navigate({ to: '/customers' });
    }
  };

  const handleSubmit = async (data: CustomerFormData) => {
    setIsLoading(true);
    handleConfirmLeave();
    try {
      await update(data as Partial<CustomerDetail>, id);
      // Pop edit; do not navigate(replace) to detail — that duplicates /customers/:id in history.
      if (canGoBack) {
        router.history.back();
      } else {
        navigate({ to: '/customers/$id', params: { id }, replace: true });
      }
    } catch (error) {
      console.error('Error updating customer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching)
    return (
      <PageSkeleton
        headerLines={1}
        subtitleLines={1}
        actions={1}
        rows={3}
        cols={1}
      />
    );

  if (!customer) return <Navigate to="/customers" replace />;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <PageHeader
        title={`${t('detail.edit')}: ${customer.firstName} ${customer.lastName}`}
        description={t('form.subTitleEdit')}
        action={{
          label: t('backToCustomers'),
          onClick: handleGoBack,
          icon: <ArrowLeft className="h-4 w-4 mr-2" />,
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('form.title')}</CardTitle>
          <CardDescription>{t('form.subEditTitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <CustomerForm
            mode="edit"
            initialData={customer}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            translate={t}
            error={(() => {
              const e = updateMutation?.error as unknown;
              if (!e) return undefined;
              if (typeof e === 'object' && e !== null) {
                const maybe = e as Record<string, unknown>;
                const resp = (maybe.response as unknown) as Record<string, unknown> | undefined;
                const data = resp?.data as Record<string, unknown> | undefined;
                if (data) {
                  const inner = data.data as Record<string, unknown> | undefined;
                  if (inner && typeof inner.error === 'string') return inner.error;
                  if (typeof data.error === 'string') return data.error;
                  if (typeof data.message === 'string') return data.message;
                  if (typeof data.error_description === 'string') return data.error_description;
                }
                if (typeof maybe.message === 'string') return maybe.message;
              }
              return String(e);
            })()}
            onDirtyChange={setIsDirty}
          />
        </CardContent>

        <SharedAlertDialog
          open={isAlertOpen}
          onOpenChange={hideAlert}
          title={t('form.discardChange')}
          description={t('form.discardChangeDesc')}
          onConfirm={handleConfirmLeave}
          onCancel={handleCancelLeave}
          variant="destructive"
          confirmText={t('form.discard')}
        />
      </Card>
    </div>
  );
};
